import "./src/styles/global.css"

// Track page navigation for referrer information
let previousPage = '';

// Function to detect traffic source based on referrer
function detectTrafficSource(referrer) {
    if (!referrer) return '';
    
    const referrerDomain = new URL(referrer).hostname.toLowerCase();
    
    // Search engines
    if (referrerDomain.includes('google.')) return 'Google';
    if (referrerDomain.includes('bing.')) return 'Bing';
    if (referrerDomain.includes('yahoo.')) return 'Yahoo';
    if (referrerDomain.includes('duckduckgo.')) return 'DuckDuckGo';
    if (referrerDomain.includes('baidu.')) return 'Baidu';
    if (referrerDomain.includes('yandex.')) return 'Yandex';
    
    // Social media
    if (referrerDomain.includes('facebook.')) return 'Facebook';
    if (referrerDomain.includes('instagram.')) return 'Instagram';
    if (referrerDomain.includes('twitter.') || referrerDomain.includes('x.com')) return 'Twitter/X';
    if (referrerDomain.includes('linkedin.')) return 'LinkedIn';
    if (referrerDomain.includes('youtube.')) return 'YouTube';
    if (referrerDomain.includes('tiktok.')) return 'TikTok';
    if (referrerDomain.includes('pinterest.')) return 'Pinterest';
    if (referrerDomain.includes('snapchat.')) return 'Snapchat';
    
    // Other common sources
    if (referrerDomain.includes('reddit.')) return 'Reddit';
    if (referrerDomain.includes('tripadvisor.')) return 'TripAdvisor';
    if (referrerDomain.includes('booking.')) return 'Booking.com';
    if (referrerDomain.includes('expedia.')) return 'Expedia';
    if (referrerDomain.includes('agoda.')) return 'Agoda';
    
    // Return the domain name if no specific match
    return referrerDomain;
}

// Function to detect organic source based on referrer
function detectOrganicSource(referrer) {
    if (!referrer) return '';
    
    const referrerDomain = new URL(referrer).hostname.toLowerCase();
    
    // Check if it's a search engine (organic traffic)
    if (referrerDomain.includes('google.') || 
        referrerDomain.includes('bing.') || 
        referrerDomain.includes('yahoo.') || 
        referrerDomain.includes('duckduckgo.') || 
        referrerDomain.includes('baidu.') || 
        referrerDomain.includes('yandex.')) {
        return 'Search Engine';
    }
    
    // Check if it's social media
    if (referrerDomain.includes('facebook.') || 
        referrerDomain.includes('instagram.') || 
        referrerDomain.includes('twitter.') || 
        referrerDomain.includes('x.com') || 
        referrerDomain.includes('linkedin.') || 
        referrerDomain.includes('youtube.') || 
        referrerDomain.includes('tiktok.') || 
        referrerDomain.includes('pinterest.') || 
        referrerDomain.includes('snapchat.')) {
        return 'Social Media';
    }
    
    // Check if it's a travel/booking site
    if (referrerDomain.includes('tripadvisor.') || 
        referrerDomain.includes('booking.') || 
        referrerDomain.includes('expedia.') || 
        referrerDomain.includes('agoda.')) {
        return 'Travel Site';
    }
    
    // Check if it's a forum/community
    if (referrerDomain.includes('reddit.')) {
        return 'Forum/Community';
    }
    
    // Default to "Direct" for other external sources
    return 'Direct';
}

export const onClientEntry = () => {
    // Apply Typekit after first render — preloaded in gatsby-ssr.js so it's in cache
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://use.typekit.net/wxp3fnb.css'
    document.head.appendChild(link)
}

export const onRouteUpdate = ({ location }) => {
    if (previousPage && previousPage !== location.pathname) {
        localStorage.setItem('previous_page', previousPage);
        sessionStorage.setItem('previous_page', previousPage);
    }
    previousPage = location.pathname;
};

export const onInitialClientRender = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource = searchParams.get('utm_source') || '';
    const utmMedium = searchParams.get('utm_medium') || '';
    const utmCampaign = searchParams.get('utm_campaign') || '';
    const utmTerm = searchParams.get('utm_term') || '';
    const utmContent = searchParams.get('utm_content') || '';
    const trafficSource = searchParams.get('traffic_source') || '';
    const organic = searchParams.get('organic') || '';
    const referrer = searchParams.get('referrer') || '';
    
    // Capture the browser's natural referrer (previous page)
    const documentReferrer = document.referrer || '';
    
    // Store UTM parameters in both sessionStorage (session-scoped) and localStorage (new-tab fallback with timestamp)
    if (utmSource || utmMedium || utmCampaign || utmTerm || utmContent || trafficSource || organic || referrer) {
        if (utmSource) { localStorage.setItem('utm_source', utmSource); sessionStorage.setItem('utm_source', utmSource); }
        if (utmMedium) { localStorage.setItem('utm_medium', utmMedium); sessionStorage.setItem('utm_medium', utmMedium); }
        if (utmCampaign) { localStorage.setItem('utm_campaign', utmCampaign); sessionStorage.setItem('utm_campaign', utmCampaign); }
        if (utmTerm) { localStorage.setItem('utm_term', utmTerm); sessionStorage.setItem('utm_term', utmTerm); }
        if (utmContent) { localStorage.setItem('utm_content', utmContent); sessionStorage.setItem('utm_content', utmContent); }
        if (trafficSource) { localStorage.setItem('traffic_source', trafficSource); sessionStorage.setItem('traffic_source', trafficSource); }
        if (organic) { localStorage.setItem('organic', organic); sessionStorage.setItem('organic', organic); }
        if (referrer) { localStorage.setItem('referrer', referrer); sessionStorage.setItem('referrer', referrer); }
        // Timestamp gates the localStorage fallback — prevents stale data from previous sessions
        localStorage.setItem('utm_captured_at', Date.now().toString());
    }
    
    // Auto-detect traffic source and organic source from UTM parameters if not provided
    if (!trafficSource && utmSource) {
        const utmTrafficSource = utmSource.charAt(0).toUpperCase() + utmSource.slice(1);
        localStorage.setItem('traffic_source', utmTrafficSource);
        sessionStorage.setItem('traffic_source', utmTrafficSource);
    }

    if (!organic && utmMedium) {
        let utmOrganicSource = '';
        switch (utmMedium.toLowerCase()) {
            case 'organic':
            case 'cpc':
            case 'ppc':
                utmOrganicSource = 'Search Engine';
                break;
            case 'social':
            case 'facebook':
            case 'instagram':
            case 'twitter':
            case 'linkedin':
            case 'youtube':
            case 'tiktok':
            case 'pinterest':
            case 'snapchat':
                utmOrganicSource = 'Social Media';
                break;
            case 'email':
                utmOrganicSource = 'Email';
                break;
            case 'referral':
                utmOrganicSource = 'Referral';
                break;
            case 'display':
            case 'banner':
                utmOrganicSource = 'Display';
                break;
            default:
                utmOrganicSource = utmMedium.charAt(0).toUpperCase() + utmMedium.slice(1);
        }
        localStorage.setItem('organic', utmOrganicSource);
        sessionStorage.setItem('organic', utmOrganicSource);
    }

    // Always store the document referrer if it exists and is from a different domain
    if (documentReferrer && documentReferrer !== window.location.origin) {
        localStorage.setItem('document_referrer', documentReferrer);
        sessionStorage.setItem('document_referrer', documentReferrer);

        const detectedTrafficSource = detectTrafficSource(documentReferrer);
        const detectedOrganicSource = detectOrganicSource(documentReferrer);

        if (!trafficSource && detectedTrafficSource) {
            localStorage.setItem('traffic_source', detectedTrafficSource);
            sessionStorage.setItem('traffic_source', detectedTrafficSource);
        }
        if (!organic && detectedOrganicSource) {
            localStorage.setItem('organic', detectedOrganicSource);
            sessionStorage.setItem('organic', detectedOrganicSource);
        }
    }
    
    // Initialize previous page tracking
    previousPage = window.location.pathname;
};
